import { useEffect } from "react";
import { supabase } from "./lib/supabase";

const PREVIEW_DELETE_KEY="arcvault-preview-deleted-profile-qualifications";
const PREVIEW_EDIT_KEY="arcvault-preview-profile-qualification-edits";

type PreviewEdit={process:string;qualification:string;welderId:string;tested:string;expires:string;certificate:string};

function field(card:HTMLElement,label:string){
  const dts=[...card.querySelectorAll("dt")];
  const dt=dts.find(x=>x.textContent?.trim().toUpperCase()===label.toUpperCase());
  return dt?.nextElementSibling?.textContent?.trim()||"";
}
function setField(card:HTMLElement,label:string,value:string){
  const dts=[...card.querySelectorAll("dt")];
  const dt=dts.find(x=>x.textContent?.trim().toUpperCase()===label.toUpperCase());
  const dd=dt?.nextElementSibling as HTMLElement|null;
  const next=value||"—";
  if(dd&&dd.textContent?.trim()!==next)dd.textContent=next;
}
function processOf(card:HTMLElement){
  const badge=card.querySelector(".process");
  if(badge?.textContent?.trim())return badge.textContent.trim();
  const firstStrong=[...card.querySelectorAll("b,strong,h3")].find(x=>/^(SMAW|FCAW|MCAW|GMAW|GTAW|SAW|PAW)$/i.test(x.textContent?.trim()||""));
  return firstStrong?.textContent?.trim()||"";
}
function qualificationOf(card:HTMLElement){
  const headings=[...card.querySelectorAll("h3")].map(x=>x.textContent?.trim()||"").filter(Boolean);
  return headings.find(x=>!/^(SMAW|FCAW|MCAW|GMAW|GTAW|SAW|PAW)$/i.test(x))||"Qualification";
}
function profileName(){
  const heading=document.querySelector<HTMLElement>(".qualified-summary h2,.qualified-summary h3");
  const text=heading?.textContent||"";
  return text.replace(/\s+is currently qualified for:.*/i,"").trim();
}
function keyFor(card:HTMLElement){return `${processOf(card)}|${field(card,"TESTED")}|${field(card,"CERTIFICATE")}`}
function originalKey(card:HTMLElement){
  if(!card.dataset.profileQualificationOriginalKey)card.dataset.profileQualificationOriginalKey=keyFor(card);
  return card.dataset.profileQualificationOriginalKey;
}
function deletedPreviewKeys(){try{return new Set<string>(JSON.parse(localStorage.getItem(PREVIEW_DELETE_KEY)||"[]"))}catch{return new Set<string>()}}
function rememberPreviewDelete(key:string){const keys=deletedPreviewKeys();keys.add(key);localStorage.setItem(PREVIEW_DELETE_KEY,JSON.stringify([...keys]))}
function previewEdits(){try{return JSON.parse(localStorage.getItem(PREVIEW_EDIT_KEY)||"{}") as Record<string,PreviewEdit>}catch{return {}}}
function savePreviewEdit(key:string,edit:PreviewEdit){const edits=previewEdits();edits[key]=edit;localStorage.setItem(PREVIEW_EDIT_KEY,JSON.stringify(edits))}
function removeSummaryChip(process:string){
  const summary=[...document.querySelectorAll(".qualified-summary span")].find(x=>(x.textContent||"").trim().startsWith(process));
  summary?.remove();
}
function applyPreviewEdit(card:HTMLElement){
  const edit=previewEdits()[originalKey(card)];if(!edit)return;
  const badge=card.querySelector<HTMLElement>(".process");
  if(badge&&badge.textContent?.trim()!==edit.process)badge.textContent=edit.process;
  const h3=[...card.querySelectorAll<HTMLElement>("h3")].find(x=>!/^(SMAW|FCAW|MCAW|GMAW|GTAW|SAW|PAW)$/i.test(x.textContent?.trim()||""));
  if(h3&&h3.textContent?.trim()!==edit.qualification)h3.textContent=edit.qualification;
  setField(card,"WELDER ID",edit.welderId);
  setField(card,"TESTED",edit.tested);
  setField(card,"EXPIRES",edit.expires);
  setField(card,"CERTIFICATE",edit.certificate);
}
function addYears(date:string,years:number){if(!date)return "";const d=new Date(date+"T00:00:00Z");d.setUTCFullYear(d.getUTCFullYear()+years);return d.toISOString().slice(0,10)}

function openPreviewEditor(card:HTMLElement){
  document.querySelector("[data-profile-edit-overlay]")?.remove();
  const key=originalKey(card),current=previewEdits()[key]||{process:processOf(card),qualification:qualificationOf(card),welderId:field(card,"WELDER ID"),tested:field(card,"TESTED"),expires:field(card,"EXPIRES"),certificate:field(card,"CERTIFICATE")};
  const overlay=document.createElement("div");overlay.className="modal-bg";overlay.dataset.profileEditOverlay="true";
  const form=document.createElement("form");form.className="modal wide-modal";
  form.innerHTML=`<button type="button" class="close" data-edit-close>×</button><p class="eyebrow">EDIT WELDER QUALIFICATION</p><h2>Edit qualification</h2><div class="form-grid"><label>Process<select name="process">${["SMAW","FCAW","MCAW","GMAW","GTAW","SAW","PAW"].map(x=>`<option${x===current.process?" selected":""}>${x}</option>`).join("")}</select></label><label>Qualification<input name="qualification" value="${current.qualification.replace(/"/g,"&quot;")}"/></label><label>Welder ID<input name="welderId" value="${current.welderId.replace(/"/g,"&quot;")}"/></label><label>Pass date<input name="tested" type="date" value="${current.tested}"/></label><label>Expiry<input name="expires" type="date" value="${current.expires}"/></label><label>Certificate number<input name="certificate" value="${current.certificate.replace(/"/g,"&quot;")}"/></label></div><div class="modal-actions"><button type="button" data-edit-close>Cancel</button><button class="primary">Save changes</button></div>`;
  const tested=form.elements.namedItem("tested") as HTMLInputElement,expires=form.elements.namedItem("expires") as HTMLInputElement;
  tested.addEventListener("change",()=>{expires.value=addYears(tested.value,2)});
  form.addEventListener("submit",e=>{e.preventDefault();const data=new FormData(form);const next={process:String(data.get("process")||""),qualification:String(data.get("qualification")||""),welderId:String(data.get("welderId")||""),tested:String(data.get("tested")||""),expires:String(data.get("expires")||""),certificate:String(data.get("certificate")||"")};savePreviewEdit(key,next);overlay.remove();window.requestAnimationFrame(()=>applyPreviewEdit(card))});
  overlay.addEventListener("click",e=>{const target=e.target as HTMLElement;if(target===overlay||target.closest("[data-edit-close]"))overlay.remove()});
  overlay.appendChild(form);document.body.appendChild(overlay);
}

function skipControlledSummary(){
  const overlay=document.querySelector<HTMLElement>(".qualification-record-overlay");
  if(!overlay||overlay.dataset.summarySkipped==="true")return;
  const eyebrow=[...overlay.querySelectorAll<HTMLElement>(".eyebrow")].find(x=>x.textContent?.trim()==="CONTROLLED WELDER QUALIFICATION");
  if(!eyebrow)return;
  const edit=[...overlay.querySelectorAll<HTMLButtonElement>("button")].find(x=>x.textContent?.trim()==="Edit record");
  if(!edit)return;
  overlay.dataset.summarySkipped="true";
  edit.click();
}

function openLiveEditor(card:HTMLElement){
  const name=profileName(),process=processOf(card),tested=field(card,"TESTED");
  if(!name||!process||!tested){window.alert("ArcVault could not identify this qualification record.");return}

  const table=document.createElement("table");
  table.style.display="none";
  const tbody=document.createElement("tbody"),row=document.createElement("tr");
  row.className="click-row";
  for(let i=0;i<7;i++)row.appendChild(document.createElement("td"));
  const nameCell=row.cells[0],nameBold=document.createElement("b");nameBold.textContent=name;nameCell.appendChild(nameBold);
  row.cells[3].textContent=process;
  row.cells[6].textContent=tested;
  tbody.appendChild(row);table.appendChild(tbody);document.body.appendChild(table);
  row.click();
  window.setTimeout(()=>table.remove(),1000);

  let attempts=0;
  const openEdit=window.setInterval(()=>{
    attempts++;
    skipControlledSummary();
    const form=document.querySelector(".qualification-record-overlay form");
    if(form){window.clearInterval(openEdit);return}
    if(attempts>=40){window.clearInterval(openEdit);window.alert("ArcVault could not open this qualification. Refresh the page and try again.")}
  },100);
}

export default function ProfileQualificationDeleteManager(){
  useEffect(()=>{
    let stopped=false,scheduled=false;
    const preview=new URLSearchParams(location.search).has("founder");

    const protectWelderNavigation=(event:Event)=>{
      const target=event.target as HTMLElement;
      const cell=target.closest<HTMLTableCellElement>("table tbody tr.click-row td");
      if(!cell||cell.cellIndex>1)return;
      const heading=[...document.querySelectorAll("h1")].find(x=>x.textContent?.trim()==="Welder Qualifications");
      const qualificationTable=heading?.closest("section.content")?.querySelector(".table-card table");
      if(!qualificationTable||!qualificationTable.contains(cell))return;
      const row=cell.closest<HTMLTableRowElement>("tr.click-row");
      if(!row)return;
      row.classList.remove("click-row");
      window.setTimeout(()=>row.classList.add("click-row"),0);
    };

    const enhance=()=>{
      scheduled=false;if(stopped)return;
      if(!preview)skipControlledSummary();
      const deleted=preview?deletedPreviewKeys():new Set<string>();
      document.querySelectorAll<HTMLElement>(".qualification-cards article").forEach(card=>{
        const key=originalKey(card);
        if(preview&&deleted.has(key)){card.remove();return}
        if(preview)applyPreviewEdit(card);
        if(card.querySelector(".profile-qualification-edit"))return;
        let actions=card.querySelector<HTMLElement>(".qualification-card-actions");
        if(!actions){actions=document.createElement("div");actions.className="qualification-card-actions";card.appendChild(actions)}
        const edit=document.createElement("button");edit.type="button";edit.className="profile-qualification-edit";edit.textContent="Edit qualification";
        edit.addEventListener("click",e=>{e.preventDefault();e.stopPropagation();preview?openPreviewEditor(card):openLiveEditor(card)});
        const button=document.createElement("button");button.type="button";button.className="danger-button profile-qualification-delete";button.textContent="Delete qualification";
        button.addEventListener("click",async e=>{
          e.preventDefault();e.stopPropagation();if(!window.confirm("Are you sure you want to delete this qualification?"))return;
          const process=processOf(card),tested=field(card,"TESTED"),certificate=field(card,"CERTIFICATE");
          if(preview){rememberPreviewDelete(originalKey(card));removeSummaryChip(process);card.remove();return}
          button.disabled=true;button.textContent="Deleting…";
          let query=supabase.from("qualifications").delete().eq("process",process);
          if(tested)query=query.eq("test_date",tested);
          if(certificate&&certificate!=="—")query=query.eq("certificate_number",certificate);
          const {error}=await query;
          if(error){window.alert(`Could not delete qualification: ${error.message}`);button.disabled=false;button.textContent="Delete qualification";return}
          removeSummaryChip(process);card.remove();window.dispatchEvent(new CustomEvent("arcvault:qualification-deleted"));
        });
        actions.append(edit,button);
      });
    };
    const scheduleEnhance=()=>{if(stopped||scheduled)return;scheduled=true;window.requestAnimationFrame(enhance)};
    document.addEventListener("click",protectWelderNavigation,true);
    enhance();
    const observer=new MutationObserver(mutations=>{
      const relevant=mutations.some(m=>[...m.addedNodes].some(node=>node instanceof HTMLElement&&(node.matches?.(".qualification-cards article,.welder-profile,.qualification-record-overlay")||node.querySelector?.(".qualification-cards article,.welder-profile,.qualification-record-overlay"))));
      if(relevant)scheduleEnhance();
    });
    observer.observe(document.body,{childList:true,subtree:true});
    return()=>{stopped=true;document.removeEventListener("click",protectWelderNavigation,true);observer.disconnect()};
  },[]);
  return null;
}
